<script lang="ts">
import {
  defineComponent,
  PropType,
  ref,
  useSlots,
  onMounted,
  onBeforeUnmount,
  watch,
  nextTick
} from 'vue'
import type { Ref } from 'vue'
import throttle from 'lodash/throttle'
import resizedWidth from '@/utilities/resizedWidth'

const EVENT_STATE = {
  DONE: 0,
  BEFORE_ENTER: 1,
  ENTER: 2,
  LEAVE: 3,
}

export default defineComponent({
  name: 'AccordionBody',

  props: {
    /**
     * アコーディオンの開閉状態制御用フラグ
     */
    openState: {
      type: Boolean,
      default: false,
    },

    /**
     * 本コンポーネントの root element のタグ名
     * dom に依存しないための属性
     *
     * @see https://jp.vuejs.org/v2/api/#is
     */
    tagName: {
      type: String,
      default: 'div',
    },

    /**
     * アコーディオン非表示時の高さの「値」
     *
     * もっと見るボタンと併用する際などに完全に非表示ではなく
     * 省略表示状態を表すために使用する
     */
    valueOfVisibleHeight: {
      type: Number,
      default: 0,
    },

    /**
     * アコーディオン非表示時の高さの「単位」
     * [制約事項] CSS アニメーションのため、「%」は不可
     * ex) rem, em, px...
     */
    unitOfVisibleHeight: {
      type: String as PropType<'px' | 'pt' | 'em' | 'rem' | 'vh'>,
      default: 'rem',
      validator: (value: string) => {
        return ['px', 'pt', 'em', 'rem', 'vh'].includes(value)
      },
    },

    /**
     * トグル時のアコーディオンアニメーションをするかどうか
     * false の場合、瞬時に開閉する
     */
    enableTransition: {
      type: Boolean,
      default: true,
    },
  },

  emits: ['transitionend', 'transitionleave'],

  setup(props, { emit }) {
    const slots = useSlots()

    /**
     * slot コンテンツの高さの値(px)
     *
     * @type {String}
     */
    const height: Ref<string | number | undefined> = ref()
    /**
     * 開閉トランジションのイベントサイクル
     */
    const eventPhase: Ref<number | null> = ref(null)

    const root = ref()

    onMounted(() => {
      const slotContents = slots.default
      if (!slotContents) return

      /**
       * 制約事項
       *
       * slot コンテンツの css.display プロパティの値に依存せずアコーディオン内の高さを
       * 適切に取得するための制約事項として、配信される slot コンテンツは 1 ルートエレメントに限定する
       *
       * @example
       *
       * [OK]
       * ```
       *   <accordion :is-show="bool">
       *    <div>...</div>
       *   </accordion>
       * ```
       *
       * [NG]
       * inline-block の場合に全体の高さの取得が困難
       *
       * ```
       *   <accordion :is-show="bool">
       *    <div style="display:inline-block">...</div>
       *    <div style="display:inline-block">...</div>
       *   </accordion>
       * ```
       */
      if (slotContents.length > 1) {
        if (import.meta.env.MODE !== 'production') {
          console.error('invalid slot contents. require single root element.')
        }
        return
      }
      // const resizeListener = () => {
      //   // 閉じている時は高さを追従する必要はないので何もしない。
      //   if (!props.openState) return
      //   if (resizedWidth()) {
      //     kickTransition()
      //   }
      // }

      window.addEventListener('resize', resizeListener)

      // slot コンテンツ dom の変更を監視して、変更があったら高さを再取得する
      // @see https://developer.mozilla.org/ja/docs/Web/API/MutationObserver
      const observer = new MutationObserver(mutations => {
        if (mutations.length) {
          kickTransition()
        }
      })

      observer.observe(root.value, {
        attributes: false,
        childList: true,
        characterData: true,
        subtree: true,
      })

      /**
       * 開くアニメーション完了後に高さを 具体値 -> auto に再設定する
       *
       * [理由]
       * slot で配信されたアコーディオンコンテンツの高さの変更に対して
       * 成り行きで高さ変更されるようにするため
       */
      const eventList = [
        'transitionend',
        'webkitTransitionEnd',
        'oTransitionEnd',
        'MSTransitionEnd',
      ]
      eventList.forEach(eventName => {
        root.value.addEventListener(eventName, () => {
          eventPhase.value = EVENT_STATE.LEAVE
        })
      })
    })

    onBeforeUnmount(() => {
      // イベントリスナー解除
      window.removeEventListener('resize', resizeListener)
      window.removeEventListener('load', kickTransition)
    })

    // window のリサイズイベントを監視して、幅に変更があったら高さを再取得する
    const resizeListener = throttle(async () => {
      // 閉じている時は高さを追従する必要はないので何もしない。
      if (!props.openState) return
      if (await resizedWidth()) {
        kickTransition()
      }
    }, 100)

    const eventCycle = async () => {
      switch (eventPhase.value) {
        case EVENT_STATE.BEFORE_ENTER:
        // 閉じる処理準備
          if (!props.openState) {
          // css トランジションのため、auto から 具体値に変更する
            await setOffsetHeight()
          }
          // next phase
          eventPhase.value = EVENT_STATE.ENTER
          break
        case EVENT_STATE.ENTER:
          await updateHeight()
          leaveEventCycleManuallyIfNeed()
          break
        case EVENT_STATE.LEAVE:
          if (props.openState) {
            height.value = 'auto'
          }
          emit('transitionleave')
          // next phase
          eventPhase.value = EVENT_STATE.DONE
      }
    }

    const kickTransition = () => {
      eventPhase.value = EVENT_STATE.BEFORE_ENTER
    }

    const updateHeight = async () => {
      // 開くアニメーション
      if (props.openState) {
        await setOffsetHeight()
      // 閉じるアニメーション
      } else {
        // イベント処理を一番後ろに回す。
        // @todo 改善したい.何を待つ必要があるのか考える.
        setTimeout(() => {
          height.value = `${props.valueOfVisibleHeight}${props.unitOfVisibleHeight}`
        }, 0)
      }
    }

    /**
     * 要素が非表示の状態では高さが常に0となってしまい、
     * `transitionend`のイベントが発生しないため、
     * 手動で終了処理を行います。
     */
    const leaveEventCycleManuallyIfNeed  = () => {
      const _height = typeof height.value === 'string'
        ? parseInt(height.value)
        : typeof height.value === 'number'
          ? height.value
          : undefined

      // 「開く時」かつ「`transitionend`が発生しないとき(= 非表示のとき = 高さ0のとき)」が対象になります。
      // 上記の条件に当てはまらない場合はここで抜けます。
      emit('transitionend')
      if (!(props.openState && _height === 0)) return
      eventPhase.value = EVENT_STATE.LEAVE
    }

    /**
     * アコーディオンコンテンツ dom の高さを取得しリアクティブデータへセットする。
     */
    const setOffsetHeight = async () => {
      await nextTick()
      // const newHeight = slots.default()[0].el.offsetHeight

      // Vue2 -> Vue3 の変更で el が取得できなくなったので、root から子要素をたどる
      const newHeight = root.value.firstElementChild.offsetHeight
      height.value = `${newHeight}px`
    }

    watch(() => props.openState, () => {
      kickTransition()
    })

    watch(() => eventPhase.value, () => {
      eventCycle()
    })
    watch(() => props.valueOfVisibleHeight, () => {
      updateHeight()
    })

    if (props.openState) {
      // コンテンツダウンロード完了後に高さを取得する
      window.addEventListener('load', kickTransition)
    } else {
      // 初期状態が閉じる状態の場合 dom がレンダリングする前に初期値をセットしておく
      height.value = `${props.valueOfVisibleHeight}${props.unitOfVisibleHeight}`
    }

    return {
      height,
      root,
    }
  },
})
</script>

<template>
  <component
    :is="tagName"
    ref="root"
    :style="{
      'height': height,
      'transition': enableTransition ? 'all 0.3s ease' : 'none',
      'overflow-y': 'hidden',
      'overflow-x': 'auto'
    }">
    <slot />
  </component>
</template>
